// Mobile keyboard handling, installed once for the whole app.
//
// Two cross-platform pain points when focusing a chat input on a phone:
//  1. iOS Safari auto-zooms when the focused input's font-size is < 16px.
//     (handled by using 16px inputs — see the chat input styles.)
//  2. The on-screen keyboard covers fixed bottom elements. iOS keeps
//     position:fixed pinned to the layout viewport (behind the keyboard);
//     Android shrinks the visual viewport. The VisualViewport API gives us the
//     real inset so we can lift the chat drawers / input above the keyboard.
//
// We publish that inset as a CSS variable `--kb-inset` on <html>, and fixed
// chat elements use `bottom: var(--kb-inset, 0px)`.

let installed = false;

export function installKeyboardInset(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  const root = document.documentElement;
  root.style.setProperty('--kb-inset', '0px');

  const vv = window.visualViewport;
  if (vv) {
    const update = (): void => {
      // Height hidden behind the keyboard (0 when it's closed).
      const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      root.style.setProperty('--kb-inset', `${inset}px`);
    };
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    update();
  }

  // After the keyboard animates in, make sure the focused field is in view
  // (some browsers don't auto-scroll fixed/overlay inputs).
  document.addEventListener('focusin', (e) => {
    const t = e.target as HTMLElement | null;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA')) {
      window.setTimeout(() => {
        t.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }, 150);
    }
  });
}
